import { authedFetch } from './http';

type Auth = { getToken: () => string | null; hydrate: () => Promise<void> };

export type LeaderboardEntry = {
  rank: number;
  userId: string;
  nickname: string;
  cap?: string;
  avatarUrl?: string;
  canton?: string;
  pts: number;
  picks: number;
  me?: boolean;
};

export type LeaderboardData = {
  scope: string;
  code?: string;
  total: number;
  limit: number;
  offset: number;
  entries: LeaderboardEntry[];
  me?: LeaderboardEntry;
};

export type MyRank = { rank: number | null; pts: number | null; picks: number | null; total: number };

export type League = {
  id: string;
  name: string;
  emoji?: string;
  banner?: string;
  ownerUserId?: string;
  members: number;
  role?: string;
  outcome: number;
  homeGoals: number;
  awayGoals: number;
  diff: number;
  exact: number;
};

export type LeagueBoard = { leagueId: string; total: number; entries: LeaderboardEntry[] };

export function getLeaderboard(
  auth: Auth,
  opts: { scope: 'swiss' | 'canton'; code?: string; limit?: number; offset?: number; search?: string },
): Promise<LeaderboardData> {
  const q = new URLSearchParams({ scope: opts.scope, tournament: 'wc2026' });
  if (opts.code) q.set('code', opts.code);
  q.set('limit', String(opts.limit ?? 20));
  q.set('offset', String(opts.offset ?? 0));
  if (opts.search) q.set('search', opts.search);
  return authedFetch(auth.getToken, auth.hydrate, `/api/leaderboard?${q}`);
}

export function getMyRank(auth: Auth): Promise<MyRank> {
  return authedFetch(auth.getToken, auth.hydrate, '/api/me/rank');
}

export async function listLeagues(auth: Auth): Promise<League[]> {
  const body = await authedFetch<{ leagues: League[] }>(auth.getToken, auth.hydrate, '/api/leagues/mine');
  return body.leagues ?? [];
}

export function getLeague(auth: Auth, id: string): Promise<League> {
  return authedFetch(auth.getToken, auth.hydrate, `/api/leagues/${id}`);
}

export function getLeagueBoard(auth: Auth, id: string): Promise<LeagueBoard> {
  return authedFetch(auth.getToken, auth.hydrate, `/api/leagues/${id}/leaderboard`);
}

export function createLeague(auth: Auth, body: { name: string; emoji?: string }): Promise<League> {
  return authedFetch(auth.getToken, auth.hydrate, '/api/leagues', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function joinLeague(auth: Auth, code: string): Promise<League> {
  return authedFetch(auth.getToken, auth.hydrate, '/api/leagues/join', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function leaveLeague(auth: Auth, id: string): Promise<void> {
  await authedFetch(auth.getToken, auth.hydrate, `/api/leagues/${id}/leave`, { method: 'POST' });
}

export async function deleteLeague(auth: Auth, id: string): Promise<void> {
  await authedFetch(auth.getToken, auth.hydrate, `/api/leagues/${id}`, { method: 'DELETE' });
}

export async function setLeagueBanner(auth: Auth, id: string, banner: string): Promise<void> {
  await authedFetch(auth.getToken, auth.hydrate, `/api/leagues/${id}/banner`, {
    method: 'PUT',
    body: JSON.stringify({ banner }),
  });
}
